/**
 * Sturdfix™ WMS - WebSocket Hook
 * Real-time updates across all connected devices
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const WS_RECONNECT_DELAY = 3000; // 3 seconds
const WS_PING_INTERVAL = 30000; // 30 seconds
const WS_MAX_RETRIES = 3; // Stop retrying after 3 failures

export function useWebSocket() {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const listenersRef = useRef([]);
  const retriesRef = useRef(0);

  const connect = useCallback(() => {
    // Stop trying after max retries
    if (retriesRef.current >= WS_MAX_RETRIES) {
      setReconnecting(false);
      return;
    }

    const token = localStorage.getItem('wms_token');
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    
    if (!wsUrl) {
      return;
    }

    const fullUrl = `${wsUrl}/ws${token ? `?token=${token}` : ''}`;
    
    try {
      wsRef.current = new WebSocket(fullUrl);
      
      wsRef.current.onopen = () => {
        retriesRef.current = 0; // Reset retries on successful connect
        setIsConnected(true);
        setReconnecting(false);
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, WS_PING_INTERVAL);
      };
      
      wsRef.current.onclose = () => {
        setIsConnected(false);
        clearInterval(pingIntervalRef.current);
        
        // Auto-reconnect with retry limit
        retriesRef.current += 1;
        if (retriesRef.current < WS_MAX_RETRIES) {
          setReconnecting(true);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, WS_RECONNECT_DELAY);
        } else {
          setReconnecting(false); // Give up gracefully
        }
      };
      
      wsRef.current.onerror = () => {};
      
      wsRef.current.onmessage = (event) => {
        if (event.data === 'pong') return;
        
        try {
          const message = JSON.parse(event.data);
          setLastMessage(message);
          
          listenersRef.current.forEach(listener => {
            try {
              listener(message);
            } catch (e) {
              // listener error — non-critical
            }
          });
        } catch (e) {
          // malformed WS message — non-critical
        }
      };
    } catch (error) {
      retriesRef.current += 1;
      if (retriesRef.current < WS_MAX_RETRIES) {
        setReconnecting(true);
        reconnectTimeoutRef.current = setTimeout(() => connect(), WS_RECONNECT_DELAY);
      } else {
        setReconnecting(false);
      }
    }
  }, []);

  const disconnect = useCallback(() => {
    clearInterval(pingIntervalRef.current);
    clearTimeout(reconnectTimeoutRef.current);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setReconnecting(false);
  }, []);

  const subscribe = useCallback((listener) => {
    listenersRef.current.push(listener);
    return () => {
      listenersRef.current = listenersRef.current.filter(l => l !== listener);
    };
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    reconnecting,
    lastMessage,
    subscribe,
    disconnect,
    reconnect: connect
  };
}

// Event handler for common WMS events
export function useWMSEvents(handlers = {}) {
  const { isConnected, reconnecting, subscribe } = useWebSocket();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const unsubscribe = subscribe((message) => {
      const { type, data } = message;
      const h = handlersRef.current;
      
      // Call specific handler if provided
      if (h[type]) {
        h[type](data);
      }
      
      // Show toasts for certain events
      switch (type) {
        case 'pallet_placed':
          if (h.onMapUpdate) h.onMapUpdate();
          break;
          
        case 'location_move':
          if (h.onMapUpdate) h.onMapUpdate();
          break;
          
        case 'qc_hold_placed':
          if (h.onMapUpdate) h.onMapUpdate();
          toast.warning(`QC Hold: ${data.pallet_ref} at ${data.slot_address}`, {
            description: data.reason
          });
          break;
          
        case 'qc_hold_cleared':
          if (h.onMapUpdate) h.onMapUpdate();
          break;
          
        case 'pallet_issued':
          if (h.onMapUpdate) h.onMapUpdate();
          if (h.onStatsUpdate) h.onStatsUpdate();
          break;
          
        case 'conflict_detected':
          if (h.onConflict) h.onConflict(data);
          toast.warning(`Sync Conflict: Slot ${data.slot_address}`, {
            description: 'Multiple pallets assigned. Please resolve.',
            duration: Infinity,
            action: {
              label: 'View',
              onClick: () => window.location.href = '/map'
            }
          });
          break;
          
        case 'abandoned_session_flagged':
          if (h.onAbandonedSession) h.onAbandonedSession(data);
          break;
          
        case 'packing_started':
          if (h.onStatsUpdate) h.onStatsUpdate();
          break;
          
        default:
          break;
      }
    });
    
    return unsubscribe;
  }, [subscribe]);

  return { isConnected, reconnecting };
}
